<div class="px-10 pt-8 pb-20 max-w-7xl mx-auto relative">

  <!-- Decorative Background Glows -->
  <div class="fixed top-1/4 -left-20 w-96 h-96 bg-[#a7a5ff]/15 rounded-full blur-[120px] -z-10 pointer-events-none"></div>
  <div class="fixed bottom-0 -right-20 w-[30rem] h-[30rem] bg-[#53ddfc]/8 rounded-full blur-[150px] -z-10 pointer-events-none"></div>

  <!-- Hero Title Section -->
  <header class="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
    <div>
      <h1 class="text-5xl font-bold tracking-tighter text-wb-text mb-2">
        Workspace
      </h1>
      <p class="text-wb-secondary max-w-md">
        Manage your cinematic creations and collaborative sequences in one immersive environment.
      </p>
    </div>
    <div class="flex items-center gap-3">
      <div class="search-box">
        <i class="pi pi-search text-xs"></i>
        <input
          type="text"
          placeholder="Search projects..."
          [ngModel]="searchQuery()"
          (ngModelChange)="searchQuery.set($event)"
          class="search-input"
        />
      </div>
      <div class="flex items-center gap-2">
        <button class="view-toggle" [class.active]="viewMode() === 'grid'" (click)="viewMode.set('grid')" title="Grid view">
          <i class="pi pi-th-large text-xs"></i>
        </button>
        <button class="view-toggle" [class.active]="viewMode() === 'list'" (click)="viewMode.set('list')" title="List view">
          <i class="pi pi-list text-xs"></i>
        </button>
      </div>
      <div class="flex gap-2">
        <a [routerLink]="companySlug && campaignSlug ? ['/', companySlug, 'campaigns', campaignSlug, 'create'] : ['/create']" class="cta-btn inline-flex items-center gap-2 rounded-full no-underline">
          <i class="pi pi-plus"></i> New Project
        </a>
        <a [routerLink]="companySlug && campaignSlug ? ['/', companySlug, 'campaigns', campaignSlug, 'import'] : ['/import']" class="cta-btn-secondary inline-flex items-center gap-2 rounded-full no-underline">
          <i class="pi pi-cloud-upload"></i> Import
        </a>
      </div>
    </div>
  </header>

  @if (service.loading()) {
    <div class="flex items-center gap-3 py-20 justify-center text-wb-secondary text-sm">
      <i class="pi pi-spin pi-spinner text-wb-accent text-xl"></i>
      Loading projects...
    </div>
  } @else if (service.projects().length === 0) {
    <!-- Empty State -->
    <div class="flex flex-col items-center py-24 text-center">
      <div class="w-20 h-20 rounded-2xl bg-wb-surface flex items-center justify-center mb-5">
        <i class="pi pi-folder-open text-3xl text-wb-muted"></i>
      </div>
      <h3 class="m-0 mb-2 text-xl font-semibold text-wb-text">No projects yet</h3>
      <p class="m-0 mb-6 text-wb-secondary text-sm max-w-xs">Create a new project or import an existing one</p>
      <div class="flex gap-3">
        <a [routerLink]="companySlug && campaignSlug ? ['/', companySlug, 'campaigns', campaignSlug, 'create'] : ['/create']" class="cta-btn inline-flex items-center gap-2 rounded-full no-underline">
          <i class="pi pi-plus"></i> New Project
        </a>
        <a [routerLink]="companySlug && campaignSlug ? ['/', companySlug, 'campaigns', campaignSlug, 'import'] : ['/import']" class="cta-btn-secondary inline-flex items-center gap-2 rounded-full no-underline">
          <i class="pi pi-cloud-upload"></i> Import
        </a>
      </div>
    </div>
  } @else {
    @if (viewMode() === 'grid') {
      <!-- Project Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        @for (project of paginatedProjects(); track project.slug) {
          <div class="project-card group glass-card rounded-xl overflow-hidden flex flex-col">
            <a [routerLink]="['/', companySlug, 'campaigns', campaignSlug, project._id, 'editor']" class="block relative h-48 overflow-hidden">
              @if (project.thumbnailUrl && !failedThumbnails().has(project.slug)) {
                <img
                  [src]="project.thumbnailUrl"
                  [alt]="project.name"
                  class="w-full object-cover group-hover:scale-110 transition-transform duration-700"
                  [class.h-full]="!maskedThumbnails().has(project.slug)"
                  [class.h-[200%]]="maskedThumbnails().has(project.slug)"
                  [class.object-top]="maskedThumbnails().has(project.slug)"
                  (error)="onThumbnailError(project.slug)"
                  (load)="onThumbnailLoad(project.slug, $event)"
                />
              } @else {
                <div class="w-full h-full thumbnail-fallback flex items-center justify-center">
                  <i class="pi pi-play text-4xl text-white/40"></i>
                </div>
              }
              <div class="absolute inset-0 bg-gradient-to-t from-[var(--wb-bg)] to-transparent opacity-60 pointer-events-none"></div>
              <div class="absolute top-4 left-4">
                <span class="status-badge">{{ project.importedAt | slice:0:10 }}</span>
              </div>
            </a>
            <div class="p-5 flex flex-col flex-grow">
              <h3 class="m-0 text-lg font-bold text-wb-text mb-2 truncate flex items-center gap-2">
                @if (project.sourceUrl !== 'local') {
                  <i class="pi pi-lock text-xs text-wb-muted" title="Imported — read-only"></i>
                }
                @if (project.lock) {
                  <i class="pi pi-user-edit text-xs text-[#60a5fa]" [title]="'Editing: ' + project.lock.userName"></i>
                }
                @if (duplicatingSlug() === project.slug) {
                  <i class="pi pi-spin pi-spinner text-xs text-wb-accent" title="Media are still being copied"></i>
                }
                <span class="truncate">{{ project.name }}</span>
              </h3>
              <div class="flex flex-wrap gap-1.5 mb-4">
                <span class="meta-tag meta-tag--primary">{{ project.fps }}fps</span>
                <span class="meta-tag meta-tag--secondary">{{ project.chaptersCount }} ch</span>
                <span class="meta-tag meta-tag--accent">{{ project.scenesCount }} sc</span>
                @if (project.imagesCount) {
                  <span class="meta-tag meta-tag--muted">{{ project.imagesCount }} img</span>
                }
              </div>
              <div class="mt-auto flex items-center gap-2">
                <a [routerLink]="['/', companySlug, 'campaigns', campaignSlug, project._id, 'editor']" class="action-btn-icon no-underline" title="Edit">
                  <i class="pi pi-pencil text-xs"></i>
                </a>
                @if (companySlug && campaignSlug) {
                  <a [routerLink]="['/', companySlug, 'campaigns', campaignSlug, project._id, 'publisher']" class="action-btn-icon no-underline" title="Publish (embed code)">
                    <i class="pi pi-share-alt text-xs"></i>
                  </a>
                }
                <button class="action-btn-icon" title="Duplicate project" (click)="openDuplicate(project, $event)">
                  <i class="pi pi-copy text-xs"></i>
                </button>
                <button class="action-btn-icon action-btn-icon--danger" title="Delete" (click)="delete(project.slug)">
                  <i class="pi pi-trash text-xs"></i>
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    } @else {
      <!-- Project List -->
      <div class="flex flex-col gap-2">
        @for (project of paginatedProjects(); track project.slug) {
          <a
            [routerLink]="['/', companySlug, 'campaigns', campaignSlug, project._id, 'editor']"
            class="project-list-row glass-card rounded-lg flex items-center gap-4 p-3 no-underline"
          >
            <div class="project-list-thumb shrink-0 w-20 h-12 rounded overflow-hidden">
              @if (project.thumbnailUrl && !failedThumbnails().has(project.slug)) {
                <img [src]="project.thumbnailUrl" [alt]="project.name" class="w-full h-full object-cover" (error)="onThumbnailError(project.slug)" />
              } @else {
                <div class="w-full h-full thumbnail-fallback flex items-center justify-center">
                  <i class="pi pi-play text-sm text-white/40"></i>
                </div>
              }
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="m-0 text-sm font-bold text-wb-text truncate flex items-center gap-1.5">
                @if (project.sourceUrl !== 'local') {
                  <i class="pi pi-lock text-[10px] text-wb-muted" title="Imported — read-only"></i>
                }
                @if (project.lock) {
                  <i class="pi pi-user-edit text-[10px] text-[#60a5fa]" [title]="'Editing: ' + project.lock.userName"></i>
                }
                @if (duplicatingSlug() === project.slug) {
                  <i class="pi pi-spin pi-spinner text-[10px] text-wb-accent" title="Media are still being copied"></i>
                }
                <span class="truncate">{{ project.name }}</span>
              </h3>
              <span class="text-[10px] text-wb-muted font-mono">{{ project.slug }}</span>
            </div>
            <div class="flex flex-wrap gap-1 shrink-0">
              <span class="meta-tag meta-tag--primary">{{ project.fps }}fps</span>
              <span class="meta-tag meta-tag--secondary">{{ project.chaptersCount }} ch</span>
              <span class="meta-tag meta-tag--accent">{{ project.scenesCount }} sc</span>
            </div>
            <span class="text-[10px] text-wb-muted shrink-0">{{ project.importedAt | slice:0:10 }}</span>
            @if (companySlug && campaignSlug) {
              <a [routerLink]="['/', companySlug, 'campaigns', campaignSlug, project._id, 'publisher']" class="action-btn-icon no-underline shrink-0" title="Publish (embed code)" (click)="$event.stopPropagation()">
                <i class="pi pi-share-alt text-xs"></i>
              </a>
            }
            <button class="action-btn-icon shrink-0" title="Duplicate project" (click)="openDuplicate(project, $event)">
              <i class="pi pi-copy text-xs"></i>
            </button>
            <button class="action-btn-icon action-btn-icon--danger shrink-0" title="Delete" (click)="delete(project.slug); $event.preventDefault(); $event.stopPropagation()">
              <i class="pi pi-trash text-xs"></i>
            </button>
          </a>
        }
      </div>
    }

    <!-- Pagination -->
    @if (totalPages() > 1) {
      <footer class="mt-16 flex items-center justify-between pt-8 pagination-border">
        <p class="text-sm text-wb-secondary m-0">
          Showing {{ rangeEnd() }} of {{ filteredProjects().length }} projects
        </p>
        <div class="flex items-center gap-2">
          <button
            class="pagination-nav"
            [disabled]="currentPage() <= 1"
            (click)="goToPage(currentPage() - 1)"
          >
            <i class="pi pi-chevron-left text-sm"></i>
          </button>
          <div class="flex items-center gap-1">
            @for (page of pageNumbers(); track $index) {
              @if (page === null) {
                <span class="px-1 text-wb-secondary select-none">...</span>
              } @else {
                <button
                  class="pagination-btn"
                  [class.active]="page === currentPage()"
                  (click)="goToPage(page)"
                >
                  {{ page }}
                </button>
              }
            }
          </div>
          <button
            class="pagination-nav"
            [disabled]="currentPage() >= totalPages()"
            (click)="goToPage(currentPage() + 1)"
          >
            <i class="pi pi-chevron-right text-sm"></i>
          </button>
        </div>
      </footer>
    }
  }
</div>

<app-duplicate-project-dialog
  [visible]="duplicateVisible()"
  (visibleChange)="onDuplicateVisibleChange($event)"
  [project]="duplicateTarget()"
  [sourceWidth]="duplicateSourceWidth()"
  [sourceHeight]="duplicateSourceHeight()"
  [takenSlugs]="takenSlugs()"
  [run]="duplicateRun()"
  [startError]="duplicateStartError()"
  [starting]="duplicateStarting()"
  (confirmed)="startDuplicate($event)"
  (openCopy)="openDuplicatedProject($event)"
/>
